import { useShow, useCustom } from '@refinedev/core'
import axios from 'axios'
import { locationDeleteErrorNotification } from '@/utils/adminFacingError'
import {
  Show,
  NumberField,
  TextFieldComponent as TextField,
} from '@refinedev/mui'
import {
  Typography,
  Stack,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Box,
} from '@mui/material'
import { useState } from 'react'
import { Link } from 'react-router-dom'

interface EntityDetail {
  uuid: string
  name: string
  regions: string[]
}

interface EntityDetailResponse {
  data: {
    entities: EntityDetail[]
    entity_type: string
    allocation_type: string
    region_name: string
  }
}

export const RegionsShow = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [modalEntityType, setModalEntityType] = useState('')
  const [modalAllocationType, setModalAllocationType] = useState('')
  const [entityDetailData, setEntityDetailData] =
    useState<EntityDetailResponse | null>(null)
  const [entityDetailLoading, setEntityDetailLoading] = useState(false)

  const apiUrl = import.meta.env.VITE_API_URL

  const { query } = useShow({
    errorNotification: (error) => {
      return {
        message: 'Error getting location',
        description:
          error?.message || 'An error occurred while fetching location',
        type: 'error',
      }
    },
  })
  const { data, isLoading } = query
  const record = data?.data

  // Fetch region statistics
  const { data: statisticsData, isLoading: statisticsLoading } = useCustom({
    url: `/regions/${record?.id}/statistics`,
    method: 'get',
    queryOptions: {
      enabled: !!record?.id,
    },
  })

  const statistics = statisticsData?.data

  const handleNumberClick = async (
    entityType: string,
    allocationType: string
  ) => {
    setModalEntityType(entityType)
    setModalAllocationType(allocationType)
    setModalOpen(true)
    setEntityDetailLoading(true)

    try {
      const url = `${apiUrl}/regions/${record?.id}/statistics/detail?entity_type=${entityType}&allocation_type=${allocationType}`
      const response = await axios.get(url)
      setEntityDetailData({ data: response.data })
    } catch (error) {
      console.error('Error fetching entity details:', error)
      setEntityDetailData(null)
    } finally {
      setEntityDetailLoading(false)
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setModalEntityType('')
    setModalAllocationType('')
    setEntityDetailData(null)
  }

  return (
    <Show
      isLoading={isLoading}
      canDelete={!record?.is_public_base}
      deleteButtonProps={{ errorNotification: locationDeleteErrorNotification }}
    >
      <Grid container spacing={3}>
        {/* Left column - existing region details */}
        <Grid item xs={12} md={6}>
          <Stack gap={1}>
            <Typography variant="body1" fontWeight="bold">
              Id
            </Typography>
            <NumberField value={record?.id ?? ''} />
            <Typography variant="body1" fontWeight="bold">
              Name
            </Typography>
            <TextField value={record?.name} />
            <Typography variant="body1" fontWeight="bold">
              Country
            </Typography>
            <TextField value={record?.country} />
            <Typography variant="body1" fontWeight="bold">
              Region
            </Typography>
            <TextField value={record?.region} />
            <Typography variant="body1" fontWeight="bold">
              Council
            </Typography>
            <TextField value={record?.council} />
            <Typography variant="body1" fontWeight="bold">
              Major (City, Town)
            </Typography>
            <TextField value={record?.major} />
            <Typography variant="body1" fontWeight="bold">
              Suburb
            </Typography>
            <TextField value={record?.suburb} />
            <Typography variant="body1" fontWeight="bold">
              Additional Names
            </Typography>
            <TextField value={record?.additional_names?.join(', ') || ''} />
          </Stack>
        </Grid>

        {/* Right column - statistics table */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Location Statistics
          </Typography>
          {statisticsLoading ? (
            <CircularProgress />
          ) : statistics ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entity Type</TableCell>
                    <TableCell align="right">Direct</TableCell>
                    <TableCell align="right">Indirect</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Organisations
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() => handleNumberClick('orgs', 'direct')}
                    >
                      {statistics.direct_orgs_count}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() => handleNumberClick('orgs', 'indirect')}
                    >
                      {statistics.indirect_orgs_count}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {statistics.total_orgs_count}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Funders
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() => handleNumberClick('funders', 'direct')}
                    >
                      {statistics.direct_funders_count}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() => handleNumberClick('funders', 'indirect')}
                    >
                      {statistics.indirect_funders_count}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {statistics.total_funders_count}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Opportunities
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() =>
                        handleNumberClick('opportunities', 'direct')
                      }
                    >
                      {statistics.direct_opportunities_count}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={() =>
                        handleNumberClick('opportunities', 'indirect')
                      }
                    >
                      {statistics.indirect_opportunities_count}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {statistics.total_opportunities_count}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" colSpan={3}>
                      Sublocations
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {statistics.subregions_count}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No statistics available</Typography>
          )}
        </Grid>
      </Grid>

      {/* Entity Detail Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {modalAllocationType &&
            modalEntityType &&
            `${
              modalAllocationType.charAt(0).toUpperCase() +
              modalAllocationType.slice(1)
            } ${
              modalEntityType.charAt(0).toUpperCase() + modalEntityType.slice(1)
            } for ${record?.name || 'Location'}`}
        </DialogTitle>
        <DialogContent>
          {entityDetailLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : entityDetailData?.data?.entities &&
            entityDetailData.data.entities.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Assigned Locations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entityDetailData?.data.entities.map((entity) => (
                    <TableRow key={entity.uuid}>
                      <TableCell>
                        <Link
                          to={`/${modalEntityType}/${entity.uuid}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          <Typography
                            sx={{
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' },
                              cursor: 'pointer',
                            }}
                          >
                            {entity.name}
                          </Typography>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {entity.regions.map((region, index) => (
                            <Chip
                              key={`${region}-${index}`}
                              label={region}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No entities found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Show>
  )
}
